import RouteInfoWithAttributes from '@ember/routing/-private/route-info-with-attributes';
import { TransformsList } from 'cio/utils/analytics/transforms';
import { ProfileType, Step } from 'cio/utils/people-merge';
import CustomerModel from 'cio/models/customer';

const MERGE_PROFILE_TYPE: Record<ProfileType, string> = {
  [ProfileType.Primary]: 'Primary',
  [ProfileType.Secondary]: 'Secondary',
  [ProfileType.Merged]: 'Merged',
};

const Transforms: TransformsList = {
  listViewedSearchedFiltered(data) {
    let analyticsData: Record<string, any> = {
      'Filter Operator': data.operator,
      'Filtered by Email Address': data.filteredByEmail,
      'Email Filter Includes Wildcard': data.emailIncludesWildcard,
      'Number of Segment Conditions': data.segments,
      'Number of Attribute Conditions': data.attributes,
      'Number of Newsletter Conditions': data.newsletters,
      'Number of Message Conditions': data.messages,
      'Number of Event Conditions': data.events,
      'Number of Relationship Attribute Conditions': data.relationship_attributes,
    };

    if (data.source) {
      analyticsData.Source = (
        {
          segment_overview: 'Segment Overview Tab',
          segment_people: 'Segment People Tab',
        } as Record<string, string>
      )[data.source];
    }

    return analyticsData;
  },

  person(data) {
    return {
      'Person ID': data.model!.id,
    };
  },

  // The segments route has two subroutes: belongs and others. Both trigger an event that uses
  // this payload, but neither have the "person" as their model. So we look for that from the
  // parent route.
  personSegmentsRouteView(data) {
    let parent = data.route!.find((route) => route.name === 'person.segments') as RouteInfoWithAttributes;
    let model = parent!.attributes as CustomerModel;

    return {
      'Person ID': model!.id,
    };
  },

  idTooltipHovered(data) {
    return {
      Source: (
        {
          'person.index': 'Profile Overview Tab',
          'person.attributes.index': 'Profile Attributes Tab',
          'person.attributes.edit': 'Profile Attributes Edit Tab',
        } as Record<string, string>
      )[data.source],
    };
  },

  mergeSetupTabViewed(data) {
    return {
      Source: (
        {
          people_list: 'People List Page',
          people_options_dropdown: 'People Options Drop-down',
          merge_success_banner: 'Merge Success Banner',
          review_and_confirm_tab: 'Review and Confirm Tab',
        } as Record<string, string>
      )[data.source],
      'Primary Profile Selected': data.model?.primary != null,
      'Secondary Profile Selected': data.model?.secondary != null,
    };
  },

  mergeSummary(data) {
    let { attributes } = data.summary;

    return {
      'Primary Attribute Count': attributes[data.customers.primary.id],
      'Secondary Attribute Count (Added)': attributes.added,
      'Secondary Attribute Count (Lost)': attributes[data.customers.secondary.id] - attributes.added,
    };
  },

  mergePersonSearched(data) {
    let eventData: Record<string, string | boolean> = {
      'Filtered by Email Address': data.filterType === 'email',
      'Filtered by Profile ID': data.filterType === 'id',
      'Filtered by Attribute Condition': data.filterType === 'condition',
      'More than Ten Matches': data.matchCount > 10,
    };

    if (data.filterType === 'condition') {
      eventData['Attribute Name'] = data.attributeName;
      eventData['Attribute Filter Operator'] = data.attributeOperator;
      eventData['Attribute Value'] = data.attributeValue;
    }

    return eventData;
  },

  mergeProfileType(data) {
    return {
      'Profile Type': MERGE_PROFILE_TYPE[data.profileType as ProfileType],
    };
  },

  mergedAttributesSearched(data) {
    return {
      Source: (
        {
          [Step.Setup]: 'Setup Tab',
          [Step.Preview]: 'Review and Confirm Tab',
        } as Record<string, string>
      )[data.source],
      'Attribute Search Value': data.searchTerm,
    };
  },

  mergeFeedbackRequestStarted(data) {
    return {
      Source: (
        {
          [Step.Preview]: 'Review and Confirm Tab',
        } as Record<string, string>
      )[data.source],
      'Feedback Type': (
        {
          review: 'Merging Options',
        } as Record<string, string>
      )[data.source],
    };
  },
};

export default Transforms;
